@file:OptIn(kotlin.uuid.ExperimentalUuidApi::class)

package com.yogaaicoach.pages.trainingprogram

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.yogaaicoach.pages.dashboard.TrainingProgramSummary
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.uuid.Uuid

private const val ErrorDaysMissing = "Please select at least one training day."

class UpdateTrainingViewModel(private val service: TrainingProgramService) : ViewModel() {
    private val _isPageLoaded = MutableStateFlow(false)
    val isPageLoaded: StateFlow<Boolean> = _isPageLoaded

    private val _selectedGoals = MutableStateFlow<Set<MainGoal>>(emptySet())
    val selectedGoals: StateFlow<Set<MainGoal>> = _selectedGoals

    private val _selectedLevel = MutableStateFlow<LevelKey?>(null)
    val selectedLevel: StateFlow<LevelKey?> = _selectedLevel

    private val _selectedAgeGroup = MutableStateFlow<AgeGroupKey?>(null)
    val selectedAgeGroup: StateFlow<AgeGroupKey?> = _selectedAgeGroup

    private val _weekdays = MutableStateFlow(Weekday.entries.map { WeekdayState(it, false, null) })
    val weekdays: StateFlow<List<WeekdayState>> = _weekdays

    private val _selectedSchedule = MutableStateFlow<List<DayAndDuration>>(emptyList())
    val selectedSchedule: StateFlow<List<DayAndDuration>> = _selectedSchedule

    private val _hasChanges = MutableStateFlow(false)
    val hasChanges: StateFlow<Boolean> = _hasChanges

    private val _scheduleErrorText = MutableStateFlow<String?>(null)
    val scheduleErrorText: StateFlow<String?> = _scheduleErrorText

    private val _isSaving = MutableStateFlow(false)
    val isSaving: StateFlow<Boolean> = _isSaving

    private var trainingProgramGuid: Uuid? = null
    private var originalGoals: Set<MainGoal> = emptySet()
    private var originalLevelKey: LevelKey? = null
    private var originalAgeGroupKey: AgeGroupKey? = null
    private var originalSchedule: List<DayAndDuration> = emptyList()

    fun initSelected(trainingProgram: TrainingProgramSummary) {
        if (_isPageLoaded.value) return
        val guid = trainingProgram.guid ?: return

        trainingProgramGuid = guid
        originalGoals = trainingProgram.goals.mapNotNull { name ->
            MainGoal.entries.firstOrNull { it.name == name }
        }.toSet()
        originalLevelKey = trainingProgram.level
        originalAgeGroupKey = trainingProgram.ageGroup
        originalSchedule = trainingProgram.schedule

        _selectedGoals.value = originalGoals
        _selectedLevel.value = originalLevelKey
        _selectedAgeGroup.value = originalAgeGroupKey
        _selectedSchedule.value = originalSchedule

        _weekdays.value = Weekday.entries.map { weekday ->
            val existing = originalSchedule.firstOrNull { it.day == weekday }
            if (existing != null) WeekdayState(weekday, true, existing.duration)
            else WeekdayState(weekday, false, null)
        }

        _isPageLoaded.value = true
    }

    fun saveChanges(onDone: () -> Unit) {
        val guid = trainingProgramGuid ?: return
        val level = _selectedLevel.value ?: return
        val ageGroup = _selectedAgeGroup.value ?: return
        if (hasScheduleErrors()) return

        _isSaving.value = true
        viewModelScope.launch {
            service.updateTrainingProgram(
                trainingProgramGuid = guid,
                selectedGoals = _selectedGoals.value,
                selectedLevel = level,
                selectedAgeGroup = ageGroup,
                schedule = _selectedSchedule.value,
            )
            _isSaving.value = false
            onDone()
        }
    }

    fun toggleSelectedGoal(key: MainGoal) {
        _selectedGoals.value = if (_selectedGoals.value.contains(key)) {
            _selectedGoals.value - key
        } else {
            _selectedGoals.value + key
        }
        refreshHasChanges()
    }

    fun selectLevel(key: LevelKey) {
        _selectedLevel.value = key
        refreshHasChanges()
    }

    fun selectAgeGroup(key: AgeGroupKey) {
        _selectedAgeGroup.value = key
        refreshHasChanges()
    }

    fun onToggleDayChecked(day: Weekday) {
        _weekdays.value = _weekdays.value.map { state ->
            if (state.weekday == day) {
                val newChecked = !state.isChecked
                state.copy(
                    isChecked = newChecked,
                    selectedDuration = if (newChecked && state.selectedDuration == null) Duration.Minutes30 else state.selectedDuration
                )
            } else state
        }
        updateSelectedSchedule()
        refreshHasChanges()
    }

    fun updateSelectedDuration(day: Weekday, duration: Duration) {
        _weekdays.value = _weekdays.value.map { state ->
            if (state.weekday == day) state.copy(selectedDuration = duration) else state
        }
        updateSelectedSchedule()
        refreshHasChanges()
    }

    private fun updateSelectedSchedule() {
        _selectedSchedule.value = _weekdays.value
            .filter { it.isChecked && it.selectedDuration != null }
            .map { DayAndDuration(it.weekday, it.selectedDuration!!) }
    }

    private fun hasScheduleErrors(): Boolean {
        _scheduleErrorText.value = if (_weekdays.value.none { it.isChecked }) ErrorDaysMissing else null
        return _scheduleErrorText.value != null
    }

    private fun refreshHasChanges() {
        val goalsChanged = _selectedGoals.value != originalGoals
        val levelChanged = _selectedLevel.value != originalLevelKey
        val ageGroupChanged = _selectedAgeGroup.value != originalAgeGroupKey
        val scheduleChanged = _selectedSchedule.value.sortedBy { it.day } != originalSchedule.sortedBy { it.day }
        _hasChanges.value = goalsChanged || levelChanged || ageGroupChanged || scheduleChanged
    }

    fun reset() {
        _isPageLoaded.value = false
        _selectedGoals.value = emptySet()
        _selectedLevel.value = null
        _selectedAgeGroup.value = null
        _weekdays.value = Weekday.entries.map { WeekdayState(it, false, null) }
        _selectedSchedule.value = emptyList()
        _hasChanges.value = false
        _scheduleErrorText.value = null
        _isSaving.value = false
        trainingProgramGuid = null
        originalGoals = emptySet()
        originalLevelKey = null
        originalAgeGroupKey = null
        originalSchedule = emptyList()
    }
}
